import {AfterContentInit, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Validators}                                                       from '@angular/forms';
import {
  ApDynformsConfigFieldset
}                                                                     from '../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {
  ApDynformsConfigTextbox
}                                                                     from '../../ap-dynforms/config/ap-dynforms-config-textbox';
import {ApDynformsComponent}                                          from '../../ap-dynforms/ap-dynforms.component';
import {ApDynformsValidator}                                          from '../../ap-dynforms/ap-dynforms-validator';
import {DriverStore}                from '../../stores/docu/driver.store';
import {FormStore, IEntryComponent} from '../../stores/layout/form.store';
import {LoginStore}                 from '../../stores/login/login.store';
import {BehaviorSubject, combineLatest, Observable, of, Subscription} from 'rxjs';
import {
  ApDynformsConfigCheckbox
}                                                                     from '../../ap-dynforms/config/ap-dynforms-config-checkbox';
import {ApDateService}                                                from '../../ap-core/services/ap-date-service';
import {DriverEditFormData}                                           from './types/ap-drivers-edit.types';
import {
  ResourcesDataService
}                                                                     from '../../services/data/resources/resources-data.service';
import {nameOf}                                                       from '../../ap-core/utils/ap-name-of';
import {map}                                                          from 'rxjs/operators';
import {MachineStore}                                                 from '../../stores/docu/machine.store';
import {ObjectFactory}                                                from 'ts-tooling';
import IDriver = Data.DocuContext.Driver.IDriver;

/**
 * Drivers edit component.
 */
@Component({
    selector: 'ap-drivers-edit',
    template: `
    <ap-dynforms [fieldsets]="formConfig" [loading$]="loading$">
      <div class="ap-form-actions" dynforms.action>
        <button id="button_drivers_edit_cancel"
                type="button"
                kendoButton class="ap-form-button-left"
                (apSingleClick)="onCancelClick()">{{ 'Global__Cancel' | translate }}
        </button>
        <button id="button_drivers_edit_save"
                type="button"
                kendoButton [themeColor]="'primary'" class="ap-form-button-right"
                [disabled]="(isSaveDisabled$ | async)"
                (apSingleClick)="onSubmitClick()">{{ 'Global__Save' | translate }}
        </button>
      </div>
    </ap-dynforms>`,
    standalone: false
})
export class ApDriversEditComponent implements OnInit, AfterContentInit, OnDestroy, IEntryComponent {
  /**
   * Implements IEntryComponent
   */
  public Caption$: BehaviorSubject<string> = new BehaviorSubject('');
  @ViewChild(ApDynformsComponent, {static: true}) dynForm: ApDynformsComponent;
  @Input() public data: IDriver | undefined;
  public formConfig: ApDynformsConfigFieldset[] = [];
  public isNewMode = false;
  public loading$: Observable<boolean> = of(true);
  public isSaveDisabled$ = new BehaviorSubject<boolean>(true);

  private _formData: DriverEditFormData | undefined;
  private _subscriptions: Subscription[] = [];

  constructor(private formStore: FormStore,
              private loginStore: LoginStore,
              private driversStore: DriverStore,
              private machineStore: MachineStore,
              private dateService: ApDateService,
              private resourcesDataService: ResourcesDataService) {
  }

  /**
   * when the component is loaded
   */
  public ngOnInit(): void {
    this.loading$ = combineLatest([
      this.machineStore.Loading$,
      this.driversStore.Saving$
    ]).pipe(
      map(loadings => loadings.some(loading => loading))
    );
    this.isNewMode = !this.data;
    this._formData = {
      FirstName: this.data?.Firstname,
      LastName: this.data?.Name,
      IsActive: this.isNewMode ? true : this.resourcesDataService.isResourceActive(this.data)
    };
    this.Caption$.next(this.isNewMode ? 'Settings__Lbl_Worker_Create' : 'Settings__Lbl_Worker_Edit');
    this.formBuilder();
  }

  public ngAfterContentInit(): void {
    this._subscriptions.push(
      this.dynForm.FormValues$.subscribe(() => {
        this.dynForm.form?.updateValueAndValidity();
        const formValues = this.dynForm.form?.getRawValue() as DriverEditFormData;
        const isSaveAvailable = JSON.stringify(formValues) !== JSON.stringify(this._formData)
          && this.dynForm.form?.valid;
        this.isSaveDisabled$.next(!isSaveAvailable);
      })
    );
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach(x => x?.unsubscribe());
  }

  /**
   * Submitting the changes.
   * Either creating a new object or updating the current.
   */
  public onSubmitClick(): void {
    const formData = this.dynForm.form.getRawValue() as DriverEditFormData;
    if (!formData) {
      return;
    }
    const driver = ObjectFactory.Copy(this.data) ?? ({} as IDriver);
    driver.FarmId = this.loginStore.SelectedFarmId;
    driver.Firstname = formData.FirstName;
    driver.Name = formData.LastName;
    driver.IsDeleted = !formData.IsActive;
    driver.DeletedAt = formData.IsActive ? undefined : this.dateService.getUtc();
    driver.DeletedBy = formData.IsActive ? undefined : this.loginStore.User.Id;
    if (this.isNewMode) {
      this.driversStore.createDriver(driver);
    } else {
      this.driversStore.updateDriver(driver);
    }
    this.formStore.closeForm();
  }

  /**
   * Cancel a clicked eventHandler - closes form.
   */
  public onCancelClick(): void {
    this.formStore.closeForm();
  }

  /**
   * binding of validators
   */
  private formBuilder(): void {
    this.formConfig = [
      new ApDynformsConfigFieldset({
        key: 'driver',
        config: [
          new ApDynformsConfigTextbox({
            key: nameOf<DriverEditFormData>('FirstName'),
            label: 'Docu_Ini__Forename',
            value: this._formData.FirstName,
            validators: [
              new ApDynformsValidator({
                validator: Validators.required,
                errorKey: 'Settings__Msg_Vali_Value_Required'
              })
            ]
          }),
          new ApDynformsConfigTextbox({
            key: nameOf<DriverEditFormData>('LastName'),
            label: 'Docu_Ini__Surname',
            value: this._formData.LastName,
            validators: [
              new ApDynformsValidator({
                validator: Validators.required,
                errorKey: 'Settings__Msg_Vali_Value_Required'
              })
            ]
          }),
          new ApDynformsConfigCheckbox({
            key: nameOf<DriverEditFormData>('IsActive'),
            label: 'Global__Active',
            value: this._formData.IsActive,
            disabled$: this.machineStore.Machines$.pipe(
              map(machines => machines.some(x => !!x.DefaultDriver && x.DefaultDriver?.Id === this.data?.Id))
            )
          })
        ]
      })
    ];
  }
}
